import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { AccountantSettingsView, AccountingFileLink, PackageList, PackageView } from "@cx/contracts";
import {
  BRAZIL_UFS,
  PACKAGE_CONTENTS,
  PACKAGE_CONTENT_LABELS,
  formatDateBR,
  shiftCompetence,
  todayInSaoPaulo,
  type PackageContent,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import {
  PACKAGE_STATUS_LABEL,
  PACKAGE_STATUS_TONE,
  competenceLabel,
  competenceOptions,
  sizeLabel,
} from "./labels";

/**
 * Contabilidade › Espaço Meu Contador (T3.5, RF-PRF-10): assistente em 3 passos (contador, conteúdo do pacote, envio
 * automático), montar e enviar o pacote do mês (ZIP com XMLs, SPED, Sintegra e relatórios) e o histórico de enviados
 * e recebidos. O papel Contador só lê e baixa.
 */

const STEPS = ["Contador", "Conteúdo do pacote", "Envio automático"] as const;
const digits = (s: string) => s.replace(/\D/g, "");

type AccountantForm = {
  name: string;
  cpf: string;
  crc: string;
  cnpj: string;
  email: string;
  phone: string;
  cep: string;
  street: string;
  number: string;
  district: string;
  cityIbge: string;
  cityName: string;
  uf: string;
};

const formOf = (s: AccountantSettingsView | undefined): AccountantForm => {
  const a = s?.accountant;
  return {
    name: a?.name ?? "",
    cpf: a?.cpf ?? "",
    crc: a?.crc ?? "",
    cnpj: a?.cnpj ?? "",
    email: a?.email ?? "",
    phone: a?.phone ?? "",
    cep: a?.address.cep ?? "",
    street: a?.address.street ?? "",
    number: a?.address.number ?? "",
    district: a?.address.district ?? "",
    cityIbge: a?.address.cityIbge ?? "",
    cityName: a?.address.cityName ?? "",
    uf: a?.address.uf ?? "",
  };
};

const useDownload = () => {
  const { api } = useSession();
  const toast = useToast();
  return useMutation({
    mutationFn: (path: string) => api<AccountingFileLink>(path),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast("danger", errorMessage(e)),
  });
};

function SetupWizard({
  settings,
  canEdit,
}: {
  settings: AccountantSettingsView;
  canEdit: boolean;
}): ReactElement {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [step, setStep] = useState(Math.min(settings.setupStep, 2));
  const [form, setForm] = useState<AccountantForm>(() => formOf(settings));
  const [contents, setContents] = useState<PackageContent[]>(settings.contents);
  const [autoDay, setAutoDay] = useState<number | null>(settings.autoDay);
  const set = (k: keyof AccountantForm) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: (body: unknown) =>
      api<AccountantSettingsView>("/accounting/settings", { method: "PUT", body: JSON.stringify(body) }),
    onSuccess: (s) => {
      qc.setQueryData(["accounting", companyId, "settings"], s);
      toast("success", step === 2 ? "Configuração concluída" : "Passo salvo");
      setStep((x) => Math.min(x + 1, 2));
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const submit = () => {
    if (step === 0)
      save.mutate({
        accountant: {
          name: form.name,
          cpf: form.cpf,
          crc: form.crc,
          cnpj: form.cnpj || null,
          email: form.email,
          phone: form.phone || null,
          address: {
            cep: form.cep || null,
            street: form.street || null,
            number: form.number || null,
            district: form.district || null,
            cityIbge: form.cityIbge || null,
            cityName: form.cityName || null,
            uf: form.uf || null,
          },
        },
      });
    else if (step === 1) save.mutate({ package: { contents } });
    else save.mutate({ schedule: { autoDay } });
  };
  const done = (k: number) => settings.setupStep > k;
  return (
    <div className="box card mb-3" data-testid="accountant-wizard">
      <div className="card-header d-flex flex-wrap gap-2 align-items-center">
        <strong className="me-auto">Configuração</strong>
        <nav aria-label="Passos da configuração" className="d-flex flex-wrap gap-1">
          {STEPS.map((label, k) => (
            <button
              key={label}
              type="button"
              className={`btn btn-sm ${step === k ? "btn-primary" : "btn-outline-dark"}`}
              aria-current={step === k ? "step" : undefined}
              disabled={k > settings.setupStep}
              onClick={() => setStep(k)}
            >
              {done(k) ? "✓ " : `${k + 1}. `}
              {label}
            </button>
          ))}
        </nav>
      </div>
      <div className="card-body">
        <fieldset disabled={!canEdit || save.isPending}>
          {step === 0 && (
            <div className="row">
              <p className="small text-muted col-12">
                Os dados do contador vão no registro 0100 do SPED e o e-mail recebe o pacote do mês.
              </p>
              <Field label="Nome" required className="col-md-6">
                <input className="form-control" value={form.name} onChange={set("name")} maxLength={100} />
              </Field>
              <Field label="CPF" required className="col-md-3">
                <input className="form-control" inputMode="numeric" value={form.cpf} onChange={set("cpf")} />
              </Field>
              <Field label="CRC" required className="col-md-3" hint="Ex.: SP-123456/O">
                <input className="form-control" value={form.crc} onChange={set("crc")} maxLength={15} />
              </Field>
              <Field label="E-mail" required className="col-md-6">
                <input type="email" className="form-control" value={form.email} onChange={set("email")} />
              </Field>
              <Field label="Telefone" className="col-md-3">
                <input className="form-control" inputMode="tel" value={form.phone} onChange={set("phone")} />
              </Field>
              <Field label="CNPJ do escritório" className="col-md-3">
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={form.cnpj}
                  onChange={set("cnpj")}
                />
              </Field>
              <Field label="CEP" className="col-md-3">
                <input className="form-control" inputMode="numeric" value={form.cep} onChange={set("cep")} />
              </Field>
              <Field label="Endereço" className="col-md-6">
                <input className="form-control" value={form.street} onChange={set("street")} maxLength={60} />
              </Field>
              <Field label="Número" className="col-md-3">
                <input className="form-control" value={form.number} onChange={set("number")} maxLength={10} />
              </Field>
              <Field label="Bairro" className="col-md-4">
                <input
                  className="form-control"
                  value={form.district}
                  onChange={set("district")}
                  maxLength={60}
                />
              </Field>
              <Field label="Município" className="col-md-4">
                <input
                  className="form-control"
                  value={form.cityName}
                  onChange={set("cityName")}
                  maxLength={60}
                />
              </Field>
              <Field label="Código IBGE" className="col-md-2">
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={form.cityIbge}
                  onChange={(e) => setForm((f) => ({ ...f, cityIbge: digits(e.target.value).slice(0, 7) }))}
                />
              </Field>
              <Field label="UF" className="col-md-2">
                <select className="form-select" value={form.uf} onChange={set("uf")}>
                  <option value="">—</option>
                  {BRAZIL_UFS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
          {step === 1 && (
            <div>
              <p className="small text-muted">O que vai no pacote do mês (pode mudar a cada envio).</p>
              {PACKAGE_CONTENTS.map((c) => (
                <div className="form-check" key={c}>
                  <input
                    id={`content-${c}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={contents.includes(c)}
                    onChange={(e) =>
                      setContents((x) => (e.target.checked ? [...x, c] : x.filter((y) => y !== c)))
                    }
                  />
                  <label className="form-check-label" htmlFor={`content-${c}`}>
                    {PACKAGE_CONTENT_LABELS[c]}
                  </label>
                </div>
              ))}
            </div>
          )}
          {step === 2 && (
            <div className="row">
              <p className="small text-muted col-12">
                No dia escolhido, o ERP monta o pacote do mês anterior e envia ao contador por e-mail.
              </p>
              <Field label="Envio automático" className="col-md-6">
                <select
                  className="form-select"
                  value={autoDay ?? ""}
                  onChange={(e) => setAutoDay(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Desligado (envio manual)</option>
                  {Array.from({ length: 28 }, (_, k) => k + 1).map((d) => (
                    <option key={d} value={d}>
                      Todo dia {d}
                    </option>
                  ))}
                </select>
              </Field>
              {settings.nextRunOn && (
                <p className="small col-12" data-testid="next-run">
                  Próximo envio: {formatDateBR(settings.nextRunOn)}
                </p>
              )}
            </div>
          )}
          {canEdit && (
            <div className="d-flex justify-content-end gap-2 mt-2">
              <button type="button" className="btn btn-primary" onClick={submit} disabled={save.isPending}>
                {step === 2 ? "Salvar" : "Salvar e continuar"}
              </button>
            </div>
          )}
        </fieldset>
      </div>
    </div>
  );
}

function SendPanel({ settings }: { settings: AccountantSettingsView }): ReactElement {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const options = competenceOptions();
  const [competence, setCompetence] = useState(shiftCompetence(todayInSaoPaulo().slice(0, 7), -1));
  const [send, setSend] = useState(!!settings.accountant);
  const [key, setKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    setSend(!!settings.accountant);
  }, [settings.accountant]);
  const build = useMutation({
    mutationFn: () =>
      api<PackageView>("/accounting/packages", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ competence, send }),
      }),
    onSuccess: (p) => {
      toast("success", `Pacote nº ${p.number} em montagem${send ? "; vai ao contador quando terminar" : ""}`);
      setKey(crypto.randomUUID());
      void qc.invalidateQueries({ queryKey: ["accounting", companyId, "packages"] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <div className="box card mb-3">
      <div className="card-header">
        <strong>Pacote do mês</strong>
      </div>
      <div className="card-body row g-2 align-items-end">
        <Field label="Competência" className="col-md-3">
          <select
            className="form-select"
            value={competence}
            onChange={(e) => setCompetence(e.target.value)}
            data-testid="package-competence"
          >
            {options.map((c) => (
              <option key={c} value={c}>
                {competenceLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <div className="col-md-5 mb-3">
          <div className="form-check">
            <input
              id="package-send"
              type="checkbox"
              className="form-check-input"
              checked={send}
              disabled={!settings.accountant}
              onChange={(e) => setSend(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="package-send">
              Enviar ao contador quando terminar
              {settings.accountant ? ` (${settings.accountant.email})` : " (cadastre o contador)"}
            </label>
          </div>
        </div>
        <div className="col-md-4 mb-3 text-md-end">
          <button
            type="button"
            className="btn btn-primary"
            disabled={build.isPending}
            onClick={() => build.mutate()}
          >
            Montar pacote
          </button>
        </div>
        <p className="small text-muted col-12 mb-0">
          Conteúdo: {settings.contents.map((c) => PACKAGE_CONTENT_LABELS[c]).join(", ")}. ZIP até 10 MB vai
          anexo; acima disso o e-mail só avisa e o contador baixa pelo ERP.
        </p>
      </div>
    </div>
  );
}

function ReceivedForm(): ReactElement {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [competence, setCompetence] = useState(shiftCompetence(todayInSaoPaulo().slice(0, 7), -1));
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("competence", competence);
      fd.append("description", description);
      fd.append("file", file!);
      return api<PackageView>("/accounting/packages/received", { method: "POST", body: fd });
    },
    onSuccess: () => {
      toast("success", "Arquivo do contador anexado");
      setDescription("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      void qc.invalidateQueries({ queryKey: ["accounting", companyId, "packages"] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <div className="row g-2 align-items-end p-3 border-top">
      <Field label="Competência" className="col-md-2">
        <select className="form-select" value={competence} onChange={(e) => setCompetence(e.target.value)}>
          {competenceOptions().map((c) => (
            <option key={c} value={c}>
              {competenceLabel(c)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Descrição" className="col-md-4">
        <input
          className="form-control"
          value={description}
          maxLength={200}
          placeholder="Ex.: Guias do mês, balancete"
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field label="Arquivo" className="col-md-4" hint="PDF, XML, ZIP, CSV, XLSX ou imagem até 10 MB.">
        <input
          ref={fileRef}
          type="file"
          className="form-control"
          data-testid="received-file"
          accept=".pdf,.xml,.zip,.csv,.xlsx,.txt,.png,.jpg,.jpeg"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </Field>
      <div className="col-md-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-primary w-100"
          disabled={!file || description.trim().length < 3 || upload.isPending}
          onClick={() => upload.mutate()}
        >
          Anexar
        </button>
      </div>
    </div>
  );
}

function History({ canSend, canReceive }: { canSend: boolean; canReceive: boolean }): ReactElement {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [direction, setDirection] = useState<"sent" | "received">("sent");
  const download = useDownload();
  const list = useQuery({
    queryKey: ["accounting", companyId, "packages", direction],
    queryFn: () => api<PackageList>(`/accounting/packages?direction=${direction}`),
    refetchInterval: (q) =>
      q.state.data?.data.some((p) => p.status === "queued" || p.status === "sending") ? 2_000 : false,
  });
  const resend = useMutation({
    mutationFn: (id: string) =>
      api<PackageView>(`/accounting/packages/${id}/send`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
      }),
    onSuccess: () => {
      toast("success", "Envio ao contador pedido");
      void qc.invalidateQueries({ queryKey: ["accounting", companyId, "packages"] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const rows = list.data?.data ?? [];
  return (
    <div className="box card">
      <div className="card-header d-flex align-items-center gap-2">
        <strong className="me-auto">Histórico</strong>
        <div className="btn-group btn-group-sm" role="group" aria-label="Enviados ou recebidos">
          <button
            type="button"
            className={`btn ${direction === "sent" ? "btn-primary" : "btn-outline-dark"}`}
            aria-pressed={direction === "sent"}
            onClick={() => setDirection("sent")}
          >
            Enviados
          </button>
          <button
            type="button"
            className={`btn ${direction === "received" ? "btn-primary" : "btn-outline-dark"}`}
            aria-pressed={direction === "received"}
            onClick={() => setDirection("received")}
          >
            Recebidos
          </button>
        </div>
      </div>
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.isSuccess && rows.length === 0 && (
        <EmptyState
          title={direction === "sent" ? "Nenhum pacote enviado" : "Nenhum arquivo recebido"}
          description={
            direction === "sent"
              ? "Monte o pacote do mês acima."
              : "Anexe aqui o que o contador mandar (guias, balancete, folha)."
          }
          actions={[]}
        />
      )}
      {rows.length > 0 && (
        <div className="table-responsive">
          <table
            className="table align-middle mb-0"
            aria-label={direction === "sent" ? "Pacotes enviados" : "Arquivos recebidos"}
          >
            <thead>
              <tr>
                <th scope="col">Nº</th>
                <th scope="col">Competência</th>
                <th scope="col">{direction === "sent" ? "Conteúdo" : "Descrição"}</th>
                <th scope="col">Situação</th>
                <th scope="col">{direction === "sent" ? "Enviado" : "Anexado"}</th>
                <th scope="col">Tamanho</th>
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} data-testid={`package-${p.number}`}>
                  <td>
                    {p.number}
                    {p.auto && <span className="badge text-bg-light ms-1">automático</span>}
                  </td>
                  <td>{competenceLabel(p.competence)}</td>
                  <td className="small">
                    {direction === "sent"
                      ? p.contents.map((c) => PACKAGE_CONTENT_LABELS[c]).join(", ")
                      : (p.description ?? p.fileName)}
                    {p.error && <div className="text-danger">{p.error}</div>}
                    {p.manifest && p.manifest.warnings.length > 0 && (
                      <details className="text-muted">
                        <summary>{p.manifest.warnings.length} aviso(s)</summary>
                        <ul className="mb-0">
                          {p.manifest.warnings.slice(0, 30).map((w) => (
                            <li key={w}>{w}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </td>
                  <td>
                    {direction === "sent" ? (
                      <StatusBadge tone={PACKAGE_STATUS_TONE[p.status]}>
                        {PACKAGE_STATUS_LABEL[p.status]}
                      </StatusBadge>
                    ) : (
                      <StatusBadge tone="success">Recebido</StatusBadge>
                    )}
                  </td>
                  <td className="small text-nowrap">
                    {direction === "sent"
                      ? p.sentAt
                        ? `${dateTime(p.sentAt)}${p.emailMode === "notice" ? " (aviso)" : ""}`
                        : "—"
                      : dateTime(p.createdAt)}
                  </td>
                  <td className="small">{sizeLabel(p.sizeBytes)}</td>
                  <td className="text-end text-nowrap">
                    {p.fileName && p.status !== "queued" && p.status !== "failed" && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={download.isPending}
                        onClick={() => download.mutate(`/accounting/packages/${p.id}/download`)}
                      >
                        Baixar
                      </button>
                    )}
                    {canSend && direction === "sent" && (p.status === "ready" || p.status === "sent") && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary ms-1"
                        disabled={resend.isPending}
                        onClick={() => resend.mutate(p.id)}
                      >
                        {p.status === "sent" ? "Enviar de novo" : "Enviar"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {direction === "received" && canReceive && <ReceivedForm />}
    </div>
  );
}

export function AccountantPage(): ReactElement {
  const { api, companyId, me } = useSession();
  const perms = me.permissions as Permissions;
  const settings = useQuery({
    queryKey: ["accounting", companyId, "settings"],
    queryFn: () => api<AccountantSettingsView>("/accounting/settings"),
  });
  return (
    <>
      <ContentHeader title="Espaço Meu Contador" breadcrumb={["Contabilidade", "Espaço Meu Contador"]} />
      <QueryStatus loading={settings.isLoading} error={settings.error} />
      {settings.data && (
        <>
          <SetupWizard
            key={settings.data.setupStep}
            settings={settings.data}
            canEdit={can(perms, "accounting", "update")}
          />
          {can(perms, "accounting", "create") && <SendPanel settings={settings.data} />}
          <History
            canSend={can(perms, "accounting", "update")}
            canReceive={can(perms, "accounting", "create")}
          />
        </>
      )}
    </>
  );
}
